"use client";

import { c as _c } from "react/compiler-runtime";
import type { ExperienceConfiguration } from "@ninetailed/experience.js-shared";
import type { ReactNode } from "react";
import { useRef } from "react";
import { useFireOnView } from "@/hooks/useFireOnView";
import { trackVariantViewed } from "@/lib/analytics/segment";
import type { VariantData } from "@/lib/ninetailed/mapEntryExperiences";
import { selectActiveVariants } from "@/lib/ninetailed/selectActiveVariants";
interface Props<TFields> {
  experiences: ExperienceConfiguration<VariantData<TFields>>[];
  exposures: Record<string, number>;
  baseline: VariantData<TFields>;
  children: ReactNode;
}

/**
 * Fires a "Variant Viewed" event for every arm the visitor was exposed to —
 * baseline (`variantIndex: 0`) and variants alike — once it scrolls into view.
 * Wired into `PersonalizedEntry` so every personalized content type is covered
 * automatically, with no per-type setup.
 *
 * The wrapper uses `display: contents` so it adds no layout box; the observer
 * targets the real rendered child element instead.
 *
 * When the selected arm renders nothing there is no child to observe, and the
 * `display: contents` wrapper never intersects — so waiting for a view event
 * would drop every exposure for that arm while its sibling arms track normally.
 * Being assigned an arm that shows nothing is still an exposure, so it fires on
 * render instead. `fireOnce` guards both paths against double-counting.
 */
export function VariantViewedTracker(t0) {
  const $ = _c(12);
  const {
    experiences,
    exposures,
    baseline,
    children
  } = t0;
  let t1;
  if ($[0] !== baseline || $[1] !== experiences || $[2] !== exposures) {
    t1 = selectActiveVariants(experiences, exposures, baseline);
    $[0] = baseline;
    $[1] = experiences;
    $[2] = exposures;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const activeVariants = t1;
  const targetRef = useRef(null);
  const hasFired = useRef(false);
  let t2;
  if ($[4] !== activeVariants) {
    t2 = function fireOnce() {
      if (hasFired.current) {
        return;
      }
      hasFired.current = true;
      for (const variant of activeVariants) {
        trackVariantViewed(variant);
      }
    };
    $[4] = activeVariants;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const fireOnce = t2;
  let t3;
  if ($[6] !== activeVariants.length || $[7] !== fireOnce) {
    t3 = function captureTarget(node) {
      const child = node?.firstElementChild;
      if (child instanceof HTMLElement) {
        targetRef.current = child;
        return;
      }
      targetRef.current = null;
      if (node && activeVariants.length > 0) {
        fireOnce();
      }
    };
    $[6] = activeVariants.length;
    $[7] = fireOnce;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const captureTarget = t3;
  useFireOnView(targetRef, fireOnce, activeVariants.length > 0);
  let t4;
  if ($[9] !== captureTarget || $[10] !== children) {
    t4 = <span className="contents" ref={captureTarget}>{children}</span>;
    $[9] = captureTarget;
    $[10] = children;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}